import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import LinkIcon from "@mui/icons-material/Link"
import DialogActions from "@mui/material/DialogActions"
import FormControl from "@mui/material/FormControl"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import RichTextEditor from "components/common/RichTextEditor"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import Typography from "@mui/material/Typography"
import Chip from "@mui/material/Chip"
import Box from "@mui/material/Box"
import FormGroup from "@mui/material/FormGroup"
import FormLabel from "@mui/material/FormLabel"
import Switch from "@mui/material/Switch"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import { exchangeArrayItem } from "utils/data"
import { Container, Draggable } from "react-smooth-dnd"
import { useSnackbar } from "notistack"
import { useForm } from "react-hook-form"
import {
  deleteProgramInnercise,
  exchangeProgramLevelIndex,
  exchangeProgramInnerciseIndex,
  addProgram,
  updateProgram,
  getProgramDetail,
  deleteProgramLevel,
  showProgramProgress,
  hideProgramProgress,
  setProgramLockable,
  cancelProgramLockable,
  getTierPrograms,
  getSimpleProgramList,
} from "services/program"
import { getProducts } from "services/products"
import {
  generateInnerciseLinkHtml,
  generateProgramLinkHtml,
} from "utils/program"
import ProgramContributorDialog from "./ProgramContributorDlg"
import ProgramLevelDialog from "./ProgramLevelDlg"
import ProgramInnerciseDialog from "./ProgramInnerciseDlg"
import ProgramCategoryDlg from "./ProgramCategoryDlg"
import ProgramSingleInnerciseControl from "./ProgramSingleInnerciseControl"
import MediaContentDisplay from "components/common/MediaManager/MediaContentDisplay"
import LoadingScreen from "components/common/LoadingScreen"
import ProgramLevelCard from "./ProgramLevelCard"
import {
  MEDIA_TYPE_INDEX,
  PROGRAM_TYPE,
  PROGRAM_TYPE_INDEX,
  RAIL_UI_TYPE,
  INNERCISE_MODE,
} from "constants/global"
import {
  StyledInputLabel,
  StyledInputLabelSmall,
  StyledRankSlider,
} from "components/Style/Forms.style"
import {
  StyledProgramLevelContainer,
  StyledProgramDialog,
} from "./Program.style"
import { set } from "lodash"
import { verifyResponse } from "utils/httpRequest"
import { listExperts, getExpertByProgram, setProgramExpert } from "services/expert"

const PROGRAM_TITLE_MAX_LENGTH = 255

const getRequestErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.message
  if (Array.isArray(message)) {
    return message.join(". ")
  }
  return message || "An unexpected error occurred. Please try again."
}

const ProgramDialog = (props) => {
  const { onSubmit, openMediaManager, categories, access, openConfirmDialog } =
    props

  const activeCategories = categories
    ? categories.filter((el) => el.subCategoryList.length > 0)
    : []

  const initProgram = {
    title: "",
    description: "",
    categoryId:
      activeCategories && activeCategories.length > 0
        ? activeCategories[0].id
        : null,
    subCategoryId:
      activeCategories && activeCategories.length > 0
        ? activeCategories[0].subCategoryList[0].id
        : null,
    thumbResourceId: null,
    introImageId: null,
    introResourceId: null,
    rank: 0,
    pubStatus: 0,
    contributorList: [],
    subCategoryList: [],
    innerciseType: PROGRAM_TYPE_INDEX.MULTIPLE,
    isBonus: 0,
    bonusParentProgramIds: [],
  }

  let navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm()

  const [program, setProgram] = useState(initProgram)
  const [products, setProducts] = useState([])
  const [selectedProductProgramIds, setSelectedProductProgramIds] = useState([])
  const [simplePrograms, setSimplePrograms] = useState([])
  const [singleInnercise, setSingleInnercise] = useState(null)
  const [levels, setLevels] = useState([])
  const [open, setOpen] = useState(false)
  const [innerciseMode, setInnerciseMode] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [thumbnailResource, setThumbnailResource] = useState(false)
  const [experts, setExperts] = useState([])
  const [selectedExpertId, setSelectedExpertId] = useState("")
  const [thumbnailRegularResource, setThumbnailRegularResource] =
    useState(false)
  const [thumbnailFullResource, setThumbnailFullResource] = useState(false)
  const [introImage, setIntroImage] = useState(false)
  const [introVideoResource, setIntroVideoResource] = useState(false)
  const [isChanged, setIsChanged] = useState(false)
  // Set of level ids whose innercise order has been changed locally and needs to be persisted.
  const [innerciseOrderDirtyLevels, setInnerciseOrderDirtyLevels] = useState(
    () => new Set()
  )
  const thumbnailResourceRef = useRef(null)
  const thumbnailRegularResourceRef = useRef(null)
  const thumbnailFullResourceRef = useRef(null)
  const introImageRef = useRef(null)
  const introVideoResourceRef = useRef(null)

  const [openProgramLevelDlg, programLevelDlg, programLevelDlgLoading] =
    ProgramLevelDialog({
      onSubmit: () => {
        refreshProgram(program.id)
      },
      id: program.id,
    })

  // Always pass the parent program's access settings into the level dialog so it can display
  // "Inherit from Program (...)" correctly.
  const openProgramLevelDlgWithProgramAccess = (level) => {
    return openProgramLevelDlg(level, {
      tierProgramId: program?.tierProgramId ?? null,
      // Level dialog currently supports a single override; pass first for back-compat.
      productProgramId: (Array.isArray(selectedProductProgramIds) && selectedProductProgramIds.length
        ? selectedProductProgramIds[0]
        : null),
      productProgramIds: Array.isArray(selectedProductProgramIds) ? selectedProductProgramIds : [],
    })
  }

  const [openContributorDialog, contributorDialog] = ProgramContributorDialog({
    currentContributorList: program.contributorList,
    selectAction: (selectedContributor) => {
      setProgram({
        ...program,
        contributorList: [...program.contributorList, selectedContributor],
      })
    },
  })

  const updateCategories = (newCategoryList) => {
    setProgram({
      ...program,
      subCategoryList: newCategoryList,
    })
  }

  const updateLockable = async () => {
    setLoading(true)
    let response = null

    if (program.isLockable === 0) {
      response = await setProgramLockable({ programIdList: [program.id] })
    } else {
      response = await cancelProgramLockable({ programIdList: [program.id] })
    }
    setLoading(false)
    verifyResponse(response)
    if (response.rtnCode === 200) {
      refreshProgram(program.id)
    } else {
      enqueueSnackbar(response.data.message, {
        variant: "warning",
      })
    }
  }

  const updateShowProgress = async () => {
    setLoading(true)
    let response = null

    if (program.isShowProgress === 0) {
      response = await showProgramProgress({ programIdList: [program.id] })
    } else {
      response = await hideProgramProgress({ programIdList: [program.id] })
    }
    setLoading(false)
    verifyResponse(response)
    if (response.rtnCode === 200) {
      refreshProgram(program.id)
    } else {
      enqueueSnackbar(response.data.message, {
        variant: "warning",
      })
    }
  }

  const [
    openProgramCategoryDlg,
    programCategoryDlg,
    programCategoryDlgLoading,
  ] = ProgramCategoryDlg({
    onSubmit: () => {
      refreshProgram(program.id)
    },
    openMediaManager: openMediaManager,
    categories: categories,
    programSubCategories: program.subCategoryList,
    onUpdate: (data) => {
      updateCategories(data)
    },
  })

  const [
    openProgramInnerciseDlg,
    programInnerciseDlg,
    programInnerciseDlgLoading,
  ] = ProgramInnerciseDialog({
    onSubmit: () => {
      refreshProgram(program.id)
    },
    openMediaManager: openMediaManager,
  })

  // For the Innercise dialog "inherit" label + correct override behavior, pass down the resolved access:
  // Program → Level → Innercise (innercise may override; otherwise inherit level; level may inherit program).
  const computeResolvedAccessForLevelId = (levelId) => {
    const programTierProgramId = program?.tierProgramId ?? null
    const programProductProgramIds = Array.isArray(selectedProductProgramIds)
      ? selectedProductProgramIds
      : []
    const level =
      Array.isArray(levels) && levelId
        ? levels.find((l) => Number(l?.id) === Number(levelId))
        : null
    const levelTierProgramId = level?.tierProgramId ?? null
    const levelProductProgramId = level?.productProgramId ?? null
    const resolvedTierProgramId = levelTierProgramId ?? programTierProgramId ?? null
    // Tier + Product Gate are independent; a level may override either (or both).
    const resolvedProductProgramId =
      levelProductProgramId ??
      (programProductProgramIds.length ? programProductProgramIds[0] : null) ??
      null
    return {
      programTierProgramId,
      programProductProgramId: programProductProgramIds.length ? programProductProgramIds[0] : null,
      programProductProgramIds,
      levelTierProgramId,
      levelProductProgramId,
      resolvedTierProgramId,
      resolvedProductProgramId,
    }
  }

  const openProgramInnerciseDlgWithAccess = (
    mode,
    pid,
    levelId,
    innerciseId
  ) => {
    return openProgramInnerciseDlg(
      mode,
      pid,
      levelId,
      innerciseId,
      computeResolvedAccessForLevelId(levelId)
    )
  }

  //--------------------------------
  // Thumbnail Resource small
  //--------------------------------

  const updateThumbnailResource = () => {
    setThumbnailResource({
      ...thumbnailResourceRef.current,
      selectAction: selectThumbnailResource,
      clearResourceAction: clearThumbnailResource,
    })
  }
  const selectThumbnailResource = () => {
    openMediaManager(
      (media) => {
        thumbnailResourceRef.current = media
        setValue("thumbResourceId", media.id)
        updateThumbnailResource()
      },
      [MEDIA_TYPE_INDEX.image]
    )
  }

  const clearThumbnailResource = () => {
    thumbnailResourceRef.current = null
    setValue("thumbResourceId", null)
    updateThumbnailResource()
  }

  //--------------------------------
  // Thumbnail Resource Regular
  //--------------------------------

  const updateThumbnailRegularResource = () => {
    setThumbnailRegularResource({
      ...thumbnailRegularResourceRef.current,
      selectAction: selectThumbnailRegularResource,
      clearResourceAction: clearThumbnailRegularResource,
    })
  }
  const selectThumbnailRegularResource = () => {
    openMediaManager(
      (media) => {
        thumbnailRegularResourceRef.current = media
        setValue("thumbRegularResourceId", media.id)
        updateThumbnailRegularResource()
      },
      [MEDIA_TYPE_INDEX.image]
    )
  }

  const clearThumbnailRegularResource = () => {
    thumbnailRegularResourceRef.current = null
    setValue("thumbRegularResourceId", null)
    updateThumbnailRegularResource()
  }

  //--------------------------------
  // Thumbnail Resource Full
  //--------------------------------

  const updateThumbnailFullResource = () => {
    setThumbnailFullResource({
      ...thumbnailFullResourceRef.current,
      selectAction: selectThumbnailFullResource,
      clearResourceAction: clearThumbnailFullResource,
    })
  }
  const selectThumbnailFullResource = () => {
    openMediaManager(
      (media) => {
        thumbnailFullResourceRef.current = media
        setValue("thumbFullResourceId", media.id)
        updateThumbnailFullResource()
      },
      [MEDIA_TYPE_INDEX.image]
    )
  }

  const clearThumbnailFullResource = () => {
    thumbnailFullResourceRef.current = null
    setValue("thumbFullResourceId", null)
    updateThumbnailFullResource()
  }

  //--------------------------------
  // Intro Image Resource
  //--------------------------------

  const updateIntroImage = () => {
    setIntroImage({
      ...introImageRef.current,
      selectAction: selectIntroImage,
      clearResourceAction: clearIntroImage,
    })
  }
  const selectIntroImage = () => {
    openMediaManager(
      (media) => {
        introImageRef.current = media
        setValue("introImageId", media.id)
        updateIntroImage()
      },
      [MEDIA_TYPE_INDEX.image]
    )
  }

  const clearIntroImage = () => {
    introImageRef.current = null
    setValue("introImageId", null)
    updateIntroImage()
  }

  //--------------------------------
  // Intro Resource
  //--------------------------------

  const updateIntroVideoResource = () => {
    setIntroVideoResource({
      ...introVideoResourceRef.current,
      selectAction: selectIntroVideoResource,
      clearResourceAction: clearIntroVideoResource,
    })
  }

  const selectIntroVideoResource = () => {
    openMediaManager(
      (media) => {
        introVideoResourceRef.current = media
        setValue("introResourceId", media.id)
        updateIntroVideoResource()
      },
      [MEDIA_TYPE_INDEX.video, MEDIA_TYPE_INDEX.audio]
    )
  }

  const clearIntroVideoResource = () => {
    introVideoResourceRef.current = null
    setValue("introResourceId", null)
    updateIntroVideoResource()
  }

  const openDeleteLevelDlg = (levelId) => {
    openConfirmDialog(async () => {
      setLoading(true)
      const response = await deleteProgramLevel(levelId)
      switch (response.data.rtnCode) {
        case 200:
          enqueueSnackbar("Program Level Deleted", {
            variant: "success",
          })
          break
        case 20062:
          enqueueSnackbar(response.data.message, {
            variant: "warning",
          })
          break
        default:
          enqueueSnackbar("Delete Program Level Fail", { variant: "warning" })
      }
      refreshProgram(program.id)
      setLoading(false)
    })
  }

  const openDeleteInnerciseDlg = (innerciseId) => {
    openConfirmDialog(async () => {
      setLoading(true)
      const response = await deleteProgramInnercise(innerciseId)

      if (response.data.rtnCode === 200) {
        enqueueSnackbar("Program Innercise Deleted", {
          variant: "success",
        })
      } else {
        enqueueSnackbar("Delete Program Innercise Fail", {
          variant: "warning",
        })
      }
      refreshProgram(program.id)
      setLoading(false)
    })
  }

  const openDlg = async ({ programId, openInnerciseMode }) => {
    reset()
    if (programId) {
      await refreshProgram(programId)
    } else {
      setProgram(initProgram)
      setSelectedProductProgramIds([])
      thumbnailResourceRef.current = null
      introImageRef.current = null
      introVideoResourceRef.current = null
      setSelectedExpertId("")
    }

    // define display section mode
    openInnerciseMode ? setInnerciseMode(true) : setInnerciseMode(false)
    // refresh thumbnail resources
    updateThumbnailResource()
    updateThumbnailRegularResource()
    updateThumbnailFullResource()
    // refresh intro image resource
    updateIntroImage()
    // refresh intro video resource
    updateIntroVideoResource()
    setIsEditMode(programId ? true : false)
    setOpen(true)

    // load experts list when dialog opens
    try {
      const expertList = await listExperts()
      const arr = Array.isArray(expertList) ? expertList : (expertList?.data || [])
      setExperts(arr)
      if (programId) {
        const cur = await getExpertByProgram(programId)
        const eid = (cur?.expertId ?? cur?.data?.expertId) || null
        setSelectedExpertId(eid || "")
      }
    } catch (e) {
      // ignore
    }
  }

  const refreshProgram = async (programId) => {
    setLoading(true)
    const resp = await getProgramDetail(programId)
    const currentProgram = resp.data.data

    const programObj = {
      id: currentProgram.id,
      title: currentProgram.title,
      description: currentProgram.description,
      thumbResourceId: currentProgram.thumbResourceId,
      thumbResourceUIList: currentProgram.thumbResourceUIList || [],
      introImageId: currentProgram.introImageId,
      introResourceId: currentProgram.introResourceId,
      isLockable: currentProgram.isLockable,
      isShowProgress: currentProgram.isShowProgress,
      pubStatus: currentProgram.pubStatus,
      rank: currentProgram.rank,
      contributorList: currentProgram.contributorList,
      subCategoryList: currentProgram.subCategoryList,
      thumbResourceDtoList: currentProgram.thumbResourceDtoList || [],
      innerciseType:
        currentProgram.innerciseType === null
          ? PROGRAM_TYPE_INDEX.MULTIPLE
          : currentProgram.innerciseType,
      isBonus: currentProgram.isBonus ? 1 : 0,
      bonusParentProgramIds: Array.isArray(currentProgram.bonusParentProgramIds)
        ? currentProgram.bonusParentProgramIds
        : [],
    }

    const thumbRegularResourceObj = programObj.thumbResourceDtoList.find(
      (item) => {
        return item.uiType === RAIL_UI_TYPE.REGULAR.value
      }
    )

    const thumbFullResourceObj = programObj.thumbResourceDtoList.find(
      (item) => {
        return item.uiType === RAIL_UI_TYPE.FULL_SCREEN.value
      }
    )

    setValue("rank", currentProgram.rank)

    setValue("thumbResourceId", currentProgram.thumbResourceId)
    thumbnailResourceRef.current = currentProgram.thumbResourceDto

    setValue(
      "thumbRegularResourceId",
      thumbRegularResourceObj ? thumbRegularResourceObj.id : null
    )
    thumbnailRegularResourceRef.current = thumbRegularResourceObj

    setValue(
      "thumbFullResourceId",
      thumbFullResourceObj ? thumbFullResourceObj.id : null
    )
    thumbnailFullResourceRef.current = thumbFullResourceObj

    setValue("introImageId", currentProgram.introImageId)
    introImageRef.current = currentProgram.introImageDto

    setValue("introResourceId", currentProgram.introResourceId)
    introVideoResourceRef.current = currentProgram.introResourceDto

    // Set tierProgramId if present
    setProgram({
      ...programObj,
      tierProgramId: currentProgram.tierProgram
        ? currentProgram.tierProgram.id
        : "",
      productProgramId: currentProgram.productProgramId || null,
      productProgramIds: Array.isArray(currentProgram.productProgramIds)
        ? currentProgram.productProgramIds
        : null,
    })
    const idsFromApi = Array.isArray(currentProgram.productProgramIds)
      ? currentProgram.productProgramIds
      : (currentProgram.productProgramId != null ? [Number(currentProgram.productProgramId)] : [])
    setSelectedProductProgramIds(
      (idsFromApi || [])
        .map((x) => Number(x))
        .filter((n) => Number.isFinite(n) && n > 0)
    )
    setLevels(currentProgram.levelList)
    // Reset innercise-order dirty tracking whenever we reload from the server,
    // so stale level ids don't linger after add/delete/refresh.
    setInnerciseOrderDirtyLevels(new Set())

    setSingleInnercise(
      currentProgram.levelList.length > 0 &&
        currentProgram.levelList[0].innerciseList.length > 0
        ? currentProgram.levelList[0].innerciseList[0]
        : null
    )
    //setTypeChanged(false)
    setLoading(false)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleAction = async (data) => {
    const thumbResourceUIList = []
    data.thumbResourceId &&
      thumbResourceUIList.push({
        uiType: RAIL_UI_TYPE.SMALL.value,
        resourceId: data.thumbResourceId,
      })

    data.thumbRegularResourceId &&
      thumbResourceUIList.push({
        uiType: RAIL_UI_TYPE.REGULAR.value,
        resourceId: data.thumbRegularResourceId,
      })

    data.thumbFullResourceId &&
      thumbResourceUIList.push({
        uiType: RAIL_UI_TYPE.FULL_SCREEN.value,
        resourceId: data.thumbFullResourceId,
      })

    // Validation: if Tier Program is the special "product" type, a Product Gate is required.
    try {
      const selectedTier = tierPrograms.find((t) => Number(t?.id) === Number(program?.tierProgramId))
      const isProductTier =
        selectedTier && String(selectedTier.type).toLowerCase() === "product"
      if (isProductTier && (!Array.isArray(selectedProductProgramIds) || selectedProductProgramIds.length === 0)) {
        enqueueSnackbar("Product Gate is required when Tier Program is set to Product.", {
          variant: "error",
        })
        setLoading(false)
        return
      }
    } catch {
      // ignore
    }

    // Validation: if this is a Bonus Program, parent program(s) are required.
    const isBonus = Number(program?.isBonus) === 1
    const bonusParentProgramIds = (Array.isArray(program?.bonusParentProgramIds) ? program.bonusParentProgramIds : [])
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0 && Number(n) !== Number(program?.id))
    if (isBonus && bonusParentProgramIds.length === 0) {
      enqueueSnackbar("Parent Program(s) are required when Bonus Program is enabled.", {
        variant: "error",
      })
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = isEditMode
        ? await updateProgram({
            ...data,
            id: program.id,
            pubStatus: program.pubStatus,
            innerciseType: program.innerciseType,
            introImageId: data.introImageId === "" ? null : data.introImageId,
            introResourceId:
              data.introResourceId === "" ? null : data.introResourceId,
            thumbResourceUIList: thumbResourceUIList,
            subCategoryList: program.subCategoryList.map(
              (subCate) => subCate.subCategoryId
            ),
            tierProgramId: program.tierProgramId || null,
            productProgramId:
              Array.isArray(selectedProductProgramIds) && selectedProductProgramIds.length
                ? selectedProductProgramIds[0]
                : null,
            productProgramIds: Array.isArray(selectedProductProgramIds) ? selectedProductProgramIds : [],
            isBonus: isBonus ? 1 : 0,
            bonusParentProgramIds: bonusParentProgramIds,
          })
        : await addProgram({
            ...data,
            programId: program.id,
            pubStatus: program.pubStatus,
            innerciseType: PROGRAM_TYPE_INDEX.MULTIPLE,
            introImageId: data.introImageId === "" ? null : data.introImageId,
            introResourceId:
              data.introResourceId === "" ? null : data.introResourceId,
            thumbResourceUIList: thumbResourceUIList,
            subCategoryList: program.subCategoryList.map(
              (subCate) => subCate.subCategoryId
            ),
            tierProgramId: program.tierProgramId || null,
            productProgramId:
              Array.isArray(selectedProductProgramIds) && selectedProductProgramIds.length
                ? selectedProductProgramIds[0]
                : null,
            productProgramIds: Array.isArray(selectedProductProgramIds) ? selectedProductProgramIds : [],
            isBonus: isBonus ? 1 : 0,
            bonusParentProgramIds: bonusParentProgramIds,
          })

      if (response.data.rtnCode === 401) {
        localStorage.clear()
        navigate("/login")
        return
      }

      if (response.data.rtnCode !== 200) {
        enqueueSnackbar(
          `${isEditMode ? "Update" : "Create"} Program Failed: ${
            response.data.message || "Please review the form and try again."
          }`,
          { variant: "error" }
        )
        return
      }

      enqueueSnackbar(isEditMode ? "Program Updated" : "New Program Created", {
        variant: "success",
      })
      try {
        const programIdSaved = isEditMode ? program.id : (response?.data?.data?.id || response?.data?.id)
        await setProgramExpert(
          programIdSaved,
          selectedExpertId ? Number(selectedExpertId) : null
        )
      } catch (e) {}

      onSubmit()
      setOpen(false)
    } catch (error) {
      enqueueSnackbar(
        `${isEditMode ? "Update" : "Create"} Program Failed: ${getRequestErrorMessage(error)}`,
        { variant: "error" }
      )
    } finally {
      setLoading(false)
    }
  }

  // Called by ProgramLevelCard when an admin drags innercises within or across levels.
  // Updates local `levels` state and marks every affected level dirty for save.
  const handleInnerciseOrderChange = (levelId, dropResult) => {
    const { addedIndex, removedIndex, payload, reorderedInnerciseList } =
      dropResult

    setLevels((prevLevels) =>
      prevLevels.map((lvl) => {
        if (lvl.id !== levelId) {
          return lvl
        }

        if (Array.isArray(reorderedInnerciseList)) {
          return { ...lvl, innerciseList: reorderedInnerciseList }
        }

        const nextInnerciseList = Array.isArray(lvl.innerciseList)
          ? [...lvl.innerciseList]
          : []

        if (removedIndex != null) {
          nextInnerciseList.splice(removedIndex, 1)
        }

        if (addedIndex != null && payload) {
          const { sourceLevelId, ...innercise } = payload
          nextInnerciseList.splice(addedIndex, 0, innercise)
        }

        return { ...lvl, innerciseList: nextInnerciseList }
      })
    )
    setInnerciseOrderDirtyLevels((prev) => {
      const next = new Set(prev)
      next.add(levelId)
      if (payload?.sourceLevelId) {
        next.add(payload.sourceLevelId)
      }
      return next
    })
    setIsChanged(true)
  }

  const isSuccessfulOrderResponse = (response) => {
    const envelope = response?.data?.rtnCode ? response.data : response
    if (Number(envelope?.rtnCode) === 200) {
      return true
    }
    return response?.status >= 200 && response?.status < 300 && !envelope?.rtnCode
  }

  const updateLevelOrder = async () => {
    try {
      setLoading(true)

      // 1. Persist level order.
      const levelResponse = await exchangeProgramLevelIndex({
        programId: program.id,
        updateIndexArray: levels.map((level, index) => ({
          id: level.id,
          index: index,
        })),
      })

      if (!isSuccessfulOrderResponse(levelResponse)) {
        enqueueSnackbar("Fail to update program level order", {
          variant: "warning",
        })
        return
      }

      // 2. Persist innercise order for every level the admin reordered.
      //    Include the target levelId on each row so lessons can move between
      //    levels while the endpoint remains backward-compatible.
      const dirtyLevelIds = Array.from(innerciseOrderDirtyLevels)
      const innerciseResults = await Promise.all(
        dirtyLevelIds.map((levelId) => {
          const levelObj = levels.find((lvl) => lvl.id === levelId)
          if (
            !levelObj ||
            !Array.isArray(levelObj.innerciseList) ||
            levelObj.innerciseList.length === 0
          ) {
            return Promise.resolve({ rtnCode: 200 })
          }
          return exchangeProgramInnerciseIndex({
            levelId,
            updateIndexArray: levelObj.innerciseList.map(
              (innercise, index) => ({
                id: innercise.id,
                index: index,
                levelId,
              })
            ),
          })
        })
      )

      const failedInnerciseSave = innerciseResults.find(
        (r) => r && !isSuccessfulOrderResponse(r)
      )
      if (failedInnerciseSave) {
        enqueueSnackbar("Fail to update innercise order", {
          variant: "warning",
        })
        return
      }

      setIsChanged(false)
      setInnerciseOrderDirtyLevels(new Set())
      enqueueSnackbar("Update order success", {
        variant: "success",
      })
    } catch (e) {
      enqueueSnackbar("Fail to update order", {
        variant: "warning",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAddProgramLevel = () => {
    openProgramLevelDlgWithProgramAccess()
  }

  const removeContributor = (contributorId) => {
    setProgram({
      ...program,
      contributorList: program.contributorList.filter(
        (contributor) => contributor.id !== contributorId
      ),
    })
  }

  const changeProgramType = (programType) => {
    setProgram({
      ...program,
      innerciseType: programType,
    })
  }

  useEffect(() => {
    setValue(
      "subCategoryList",
      program.subCategoryList.length > 0 ? true : undefined
    )
  }, [program.subCategoryList])

  // Add state for tier programs
  const [tierPrograms, setTierPrograms] = useState([])

  // Fetch tier programs on mount (or when dialog opens)
  useEffect(() => {
    if (open) {
      getTierPrograms().then(data => {
        setTierPrograms(Array.isArray(data) ? data : [])
      })
      getProducts().then(arr => setProducts(Array.isArray(arr) ? arr : [])).catch(() => setProducts([]))
      getSimpleProgramList()
        .then((resp) => {
          const list = resp?.data?.programs || resp?.data?.data?.programs || resp?.programs || []
          setSimplePrograms(Array.isArray(list) ? list : [])
        })
        .catch(() => setSimplePrograms([]))
    }
  }, [open])

  const dlg = loading ? (
    "null"
  ) : (
    <StyledProgramDialog
      open={open}
      fullWidth={true}
      maxWidth={"lg"}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {innerciseMode
                ? "Innercise Settings"
                : isEditMode
                ? "Edit Program"
                : "Add New Program"}
            </h2>
            <p className="dialog-subtitle">
              {innerciseMode
                ? "Manage levels, lessons, and content order for this program."
                : "Update program metadata, access controls, and media assets."}
            </p>
          </div>

          {innerciseMode &&
            program.innerciseType === PROGRAM_TYPE_INDEX.MULTIPLE &&
            access.addLevelAccess && (
              <Button
                className="dialog-secondary-btn"
                variant="outlined"
                size="medium"
                color="success"
                startIcon={<AddIcon />}
                onClick={handleAddProgramLevel}
              >
                Add new Level
              </Button>
            )}
        </DialogTitle>
        <DialogContent className="dialog-content">
          {!innerciseMode && isEditMode && (
            <Button
              className="dialog-secondary-btn"
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={async () => {
                const htmlLink = generateProgramLinkHtml({
                  title: program.title,
                })
                await navigator.clipboard.writeText(htmlLink)
              }}
            >
              Copy Deep Link Html
            </Button>
          )}
          {!innerciseMode && (
            <>
              <div className="form-control-row">
                <FormControl sx={{ m: 1, width: "100%" }}>
                  <TextField
                    autoFocus
                    autoComplete="off"
                    margin="dense"
                    id="program-title"
                    label="Title"
                    fullWidth
                    defaultValue={program.title}
                    inputProps={{ maxLength: PROGRAM_TITLE_MAX_LENGTH }}
                    {...register("title", {
                      required: "Title is required",
                      maxLength: {
                        value: PROGRAM_TITLE_MAX_LENGTH,
                        message: `Title cannot exceed ${PROGRAM_TITLE_MAX_LENGTH} characters`,
                      },
                    })}
                    error={!!errors?.title}
                    helperText={
                      errors?.title?.message ||
                      `${watch("title", program.title || "").length}/${PROGRAM_TITLE_MAX_LENGTH} characters`
                    }
                    variant="standard"
                  />
                </FormControl>
              </div>

              <div className="form-control-row ">
                <FormControl sx={{ m: 1, width: "100%" }}>
                  <RichTextEditor
                    value={watch("description") || program.description || ''}
                    onChange={(value) => {
                      setValue("description", value)
                      setProgram({ ...program, description: value })
                    }}
                    label="Description"
                    placeholder="Enter program description..."
                    toolbarConfig="description"
                    minHeight="150px"
                    error={!!errors?.description}
                    helperText={!!errors?.description ? "* Description is required" : ""}
                  />
                </FormControl>
              </div>

              <div className="title-bar">
                <label>Category</label>
                <Button
                  size="small"
                  variant="contained"
                  onClick={openProgramCategoryDlg}
                  startIcon={
                    program.subCategoryList.length === 0 ? (
                      <AddIcon />
                    ) : (
                      <EditIcon />
                    )
                  }
                >
                  {program.subCategoryList.length === 0 ? "Add" : "Edit"}
                </Button>
              </div>
              <div>
                <div className="form-control-row category-section">
                  <input
                    type="hidden"
                    defaultValue={undefined}
                    {...register("subCategoryList", { required: false })}
                  />
                  {program.subCategoryList.length === 0 && "No Category"}
                  {program.subCategoryList.map((cate) => (
                    <div
                      key={`category-list-item-${cate.subCategoryId}`}
                      className="category-item"
                    >{`${cate.categoryName}-${cate.subCategoryName}`}</div>
                  ))}
                </div>
                <div className="error-message">
                  {!!errors?.subCategoryList
                    ? "* You need as least one category"
                    : ""}
                </div>
              </div>

              <StyledInputLabel>Contributors</StyledInputLabel>
              <div className="form-control-row contributor-section">
                {program.contributorList &&
                  program.contributorList.map((contributor) => (
                    <Chip
                      key={`chip-contributor-item-${contributor.id}`}
                      label={contributor.userName}
                      onDelete={() => {
                        removeContributor(contributor.id)
                      }}
                    />
                  ))}
                <Button
                  onClick={() => {
                    openContributorDialog()
                  }}
                  size="small"
                  variant="contained"
                >
                  Add Contributor
                </Button>
              </div>

              {isEditMode && (
                <div className="form-control-row two-col">
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          defaultChecked={program.isLockable}
                          color="warning"
                          onChange={(e) => {
                            updateLockable()
                          }}
                        />
                      }
                      label="Lockable"
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          defaultChecked={program.isShowProgress}
                          color="warning"
                          onChange={() => {
                            updateShowProgress()
                          }}
                        />
                      }
                      label="Show Progress"
                    />
                  </div>
                </div>
              )}

              <div className="form-control-row two-col">
                {isEditMode && (
                  <FormGroup>
                    <FormLabel>Status</FormLabel>
                    <Typography className="control-label" gutterBottom>
                      {program.pubStatus === 0 ? "Unpublished" : "Published"}
                    </Typography>
                  </FormGroup>
                )}
                <Box sx={{ width: 300 }}>
                  <Typography className="control-label" gutterBottom>
                    Program Rank {watch("rank")}
                  </Typography>
                  <StyledRankSlider
                    aria-label="program-rank"
                    getAriaValueText={(value) => {
                      set("rank", value)
                      return `${value}`
                    }}
                    defaultValue={program.rank}
                    {...register("rank", { required: true })}
                    step={1}
                    min={0}
                    max={10}
                  />
                </Box>
              </div>
              <div className="form-control-row two-col">
                <div>
                  <StyledInputLabel>
                    Thumbnail Small{" "}
                    <StyledInputLabelSmall>(4:3)</StyledInputLabelSmall>
                  </StyledInputLabel>
                  <input
                    type="hidden"
                    value={watch("thumbResourceId") || ""}
                    {...register("thumbResourceId", { required: true })}
                  />
                  <MediaContentDisplay resource={thumbnailResource} />
                  <div className="error-message">
                    {!!errors?.thumbResourceId ? "* Resource is required" : " "}
                  </div>
                </div>
                <div>
                  <StyledInputLabel>
                    Thumbnail Regular{" "}
                    <StyledInputLabelSmall>(3:4)</StyledInputLabelSmall>
                  </StyledInputLabel>
                  <input
                    type="hidden"
                    value={watch("thumbRegularResourceId") || ""}
                    {...register("thumbRegularResourceId", { required: false })}
                  />
                  <MediaContentDisplay resource={thumbnailRegularResource} />
                  <div className="error-message">
                    {!!errors?.thumbRegularResourceId ? "* Resource is required" : " "}
                  </div>
                </div>
                <div>
                  <StyledInputLabel>
                    Thumbnail Full{" "}
                    <StyledInputLabelSmall>(16:9)</StyledInputLabelSmall>
                  </StyledInputLabel>
                  <input
                    type="hidden"
                    value={watch("thumbFullResourceId") || ""}
                    {...register("thumbFullResourceId", { required: false })}
                  />
                  <MediaContentDisplay resource={thumbnailFullResource} />
                  <div className="error-message">
                    {!!errors?.thumbFullResourceId ? "* Resource is required" : " "}
                  </div>
                </div>
              </div>

              <div className="form-control-row two-col">
                <div>
                  <StyledInputLabel>Intro Image</StyledInputLabel>
                  <input
                    type="hidden"
                    value={watch("introImageId") || ""}
                    {...register("introImageId", { required: false })}
                  />
                  <MediaContentDisplay resource={introImage} />
                </div>

                <div>
                  <StyledInputLabel>Intro Video</StyledInputLabel>
                  <MediaContentDisplay resource={introVideoResource} />
                </div>
              </div>

              {isEditMode && (
                <>
                  <StyledInputLabel>Program Type</StyledInputLabel>
                  <FormControl sx={{ m: 1, minWidth: 230 }}>
                    <InputLabel id="demo-simple-select-label">Type</InputLabel>
                    <Select
                      fullWidth
                      labelId="program-category-filter-select"
                      id="program-category-filter-select"
                      value={program.innerciseType}
                      label="Type"
                      onChange={(e) => {
                        changeProgramType(e.target.value)
                      }}
                      disabled={
                        !(
                          levels.length === 0 ||
                          (levels.length === 1 &&
                            levels[0].innerciseList.length <= 1)
                        )
                      }
                    >
                      {Object.values(PROGRAM_TYPE).map((categoryType) => (
                        <MenuItem
                          key={`category-type-select-item-${categoryType.value}`}
                          value={categoryType.value}
                        >
                          {categoryType.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!(
                    levels.length === 0 ||
                    (levels.length === 1 && levels[0].innerciseList.length <= 1)
                  ) && (
                    <div className="error-message">
                      * Only empty program or one innercise program can be
                      turning into single mode
                    </div>
                  )}
                </>
              )}
              <>
                <StyledInputLabel>Tier / Product Access</StyledInputLabel>
                <FormControl sx={{ m: 1, minWidth: 230 }}>
                  <InputLabel id="tier-program-select-label">
                    Tier Program
                  </InputLabel>
                  <Select
                    fullWidth
                    labelId="tier-program-select-label"
                    id="tier-program-select"
                    value={program.tierProgramId || ""}
                    label="Tier Program"
                    onChange={e => {
                      const nextId = typeof e.target.value === 'string' ? Number(e.target.value) : e.target.value
                      setProgram({ ...program, tierProgramId: nextId })
                    }}
                  >
                    {[...tierPrograms]
                      .sort((a, b) => {
                        const order = { Free: 0, Silver: 1, Gold: 2, Platinum: 3, product: 4 };
                        const aIdx = order[a?.type] ?? 99;
                        const bIdx = order[b?.type] ?? 99;
                        return aIdx - bIdx;
                      })
                      .map(tier => (
                        <MenuItem key={tier.id} value={tier.id}>
                          {tier.type === "product" ? "Product (select below)" : tier.type}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                      <FormControl sx={{ m: 1, minWidth: 230 }}>
                  <InputLabel id="product-select-label">Product Gate</InputLabel>
                        <Select
                          multiple
                          fullWidth
                          labelId="product-select-label"
                          id="product-select"
                          value={Array.isArray(selectedProductProgramIds) ? selectedProductProgramIds : []}
                    label="Product Gate"
                          renderValue={(selected) => {
                            const ids = Array.isArray(selected) ? selected : []
                            if (!ids.length) return "None"
                            const names = ids
                              .map((id) => products.find((p) => Number(p.programId) === Number(id))?.name || `#${id}`)
                            return names.join(", ")
                          }}
                          onChange={(e) => {
                      const v = e.target.value
                      const next = Array.isArray(v) ? v : (v ? [v] : [])
                      setSelectedProductProgramIds(
                        next
                          .map((x) => Number(x))
                          .filter((n) => Number.isFinite(n) && n > 0)
                      )
                          }}
                        >
                          {products.map((p) => (
                            <MenuItem key={p.programId} value={p.programId}>
                              <Checkbox
                                checked={
                                  Array.isArray(selectedProductProgramIds) &&
                                  selectedProductProgramIds.includes(Number(p.programId))
                                }
                              />
                              <ListItemText primary={p.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                <StyledInputLabel>Bonus Program</StyledInputLabel>
                <div className="form-control-row two-col">
                  <div>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Number(program?.isBonus) === 1}
                          color="secondary"
                          onChange={(e) => {
                            const checked = !!e.target.checked
                            setProgram({
                              ...program,
                              isBonus: checked ? 1 : 0,
                              bonusParentProgramIds: checked ? (program.bonusParentProgramIds || []) : [],
                            })
                          }}
                        />
                      }
                      label="Bonus Program"
                    />
                  </div>
                </div>

                {Number(program?.isBonus) === 1 && (
                  <FormControl sx={{ m: 1, minWidth: 300 }}>
                    <InputLabel id="bonus-parent-select-label">Parent Program(s)</InputLabel>
                    <Select
                      multiple
                      fullWidth
                      labelId="bonus-parent-select-label"
                      id="bonus-parent-select"
                      value={Array.isArray(program?.bonusParentProgramIds) ? program.bonusParentProgramIds : []}
                      label="Parent Program(s)"
                      renderValue={(selected) => {
                        const ids = Array.isArray(selected) ? selected : []
                        if (!ids.length) return "None"
                        const names = ids
                          .map((id) => simplePrograms.find((p) => Number(p.id) === Number(id))?.title || `#${id}`)
                        return names.join(", ")
                      }}
                      onChange={(e) => {
                        const v = e.target.value
                        const next = Array.isArray(v) ? v : (v ? [v] : [])
                        setProgram({
                          ...program,
                          bonusParentProgramIds: next
                            .map((x) => Number(x))
                            .filter((n) => Number.isFinite(n) && n > 0 && Number(n) !== Number(program?.id)),
                        })
                      }}
                    >
                      {(Array.isArray(simplePrograms) ? simplePrograms : [])
                        .filter((p) => Number(p?.id) !== Number(program?.id))
                        .filter((p) => Number(p?.isBonus) !== 1)
                        .map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            <Checkbox
                              checked={
                                Array.isArray(program?.bonusParentProgramIds) &&
                                program.bonusParentProgramIds.includes(Number(p.id))
                              }
                            />
                            <ListItemText primary={p.title} />
                          </MenuItem>
                        ))}
                    </Select>
                    <div className="error-message">
                      {(Array.isArray(program?.bonusParentProgramIds) ? program.bonusParentProgramIds.length : 0) === 0
                        ? "* Select at least one parent program"
                        : " "}
                    </div>
                  </FormControl>
                )}

                <StyledInputLabel>Expert</StyledInputLabel>
                <FormControl sx={{ m: 1, minWidth: 230 }}>
                  <InputLabel id="expert-select-label">Expert</InputLabel>
                  <Select
                    fullWidth
                    labelId="expert-select-label"
                    id="expert-select"
                    value={selectedExpertId || ""}
                    label="Expert"
                    onChange={(e) => setSelectedExpertId(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    {experts.map((ex) => (
                      <MenuItem key={ex.id} value={String(ex.id)}>
                        {ex.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </>
            </>
          )}

          {innerciseMode && (
            <>
              <div className="action-bar">
                {program.innerciseType === PROGRAM_TYPE_INDEX.SINGLE && (
                  <>
                    {levels.length > 0 && levels[0].innerciseList.length > 0 ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={() => {
                            openProgramInnerciseDlgWithAccess(
                              INNERCISE_MODE.INNERCISE.value,
                              program.id,
                              levels[0].id,
                              levels[0].innerciseList[0].id
                            )
                          }}
                        >
                          Edit Innercise
                        </Button>

                        <Tooltip
                          title="Copy Deep Link Html"
                          style={{ marginLeft: "10px" }}
                        >
                          <IconButton
                            sx={{ color: "#182b06" }}
                            aria-label="copy innercise share link"
                            component="label"
                            onClick={async () => {
                              const htmlLink = generateInnerciseLinkHtml({
                                title: levels[0].innerciseList[0].title,
                              })
                              await navigator.clipboard.writeText(htmlLink)
                            }}
                            size="small"
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          openProgramInnerciseDlgWithAccess(
                            INNERCISE_MODE.INNERCISE.value,
                            program.id,
                            0,
                            null
                          )
                        }}
                        startIcon={<AddIcon />}
                      >
                        Add Innercise
                      </Button>
                    )}
                  </>
                )}
              </div>

              {program.innerciseType === PROGRAM_TYPE_INDEX.MULTIPLE && (
                <StyledProgramLevelContainer>
                  {access.exchangeLevelIndexAccess ? (
                    <Container
                      // Keep level drag from stealing gestures meant for
                      // innercise reordering / action buttons inside each card.
                      nonDragAreaSelector=".innercise-list, .innercise-header, .MuiIconButton-root, button, a"
                      onDrop={async (e) => {
                        setIsChanged(true)
                        const updateIndexArray = exchangeArrayItem({
                          addedIndex: e.addedIndex,
                          removedIndex: e.removedIndex,
                          indexString: "levelIndex",
                          list: levels,
                        })
                        setLevels(updateIndexArray)
                      }}
                    >
                      {levels.map((level, index) => (
                        <Draggable key={level.id}>
                          <ProgramLevelCard
                            key={`program-level-card-${level.id}`}
                            programId={program.id}
                            rank={index + 1}
                            level={level}
                            access={access}
                            openProgramInnerciseDlg={openProgramInnerciseDlgWithAccess}
                            openProgramLevelDlg={openProgramLevelDlgWithProgramAccess}
                            openDeleteLevelDlg={openDeleteLevelDlg}
                            deleteProgramInnerciseAction={
                              openDeleteInnerciseDlg
                            }
                            onInnerciseOrderChange={handleInnerciseOrderChange}
                          />
                        </Draggable>
                      ))}
                    </Container>
                  ) : (
                    <>
                      {levels.map((level, index) => (
                        <ProgramLevelCard
                          key={`program-level-card-${level.id}`}
                          programId={program.id}
                          rank={index + 1}
                          level={level}
                          access={access}
                          openProgramInnerciseDlg={openProgramInnerciseDlgWithAccess}
                          openProgramLevelDlg={openProgramLevelDlgWithProgramAccess}
                          openDeleteLevelDlg={openDeleteLevelDlg}
                          deleteProgramInnerciseAction={openDeleteInnerciseDlg}
                          onInnerciseOrderChange={handleInnerciseOrderChange}
                        />
                      ))}
                    </>
                  )}
                </StyledProgramLevelContainer>
              )}

              {program.innerciseType === PROGRAM_TYPE_INDEX.SINGLE && (
                <ProgramSingleInnerciseControl
                  innercise={singleInnercise}
                  openMediaManager={openMediaManager}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {innerciseMode && isChanged && (
            <Button
              className="dialog-submit-btn"
              variant="contained"
              color="primary"
              size="medium"
              onClick={updateLevelOrder}
            >
              Update order
            </Button>
          )}
          <Button
            className="dialog-cancel-btn"
            autoFocus
            variant="outlined"
            size="medium"
            onClick={handleClose}
          >
            Close
          </Button>
          {!innerciseMode && (
            <>
              <Button
                className="dialog-submit-btn"
                type="submit"
                variant="contained"
                size="medium"
                autoFocus
              >
                {isEditMode ? "Update" : "Create"}
              </Button>
            </>
          )}
        </DialogActions>
      </form>
      {programLevelDlg}
      {programInnerciseDlg}
      {contributorDialog}
      {programCategoryDlg}
      <LoadingScreen
        loading={
          loading || programInnerciseDlgLoading || programLevelDlgLoading
        }
      />
    </StyledProgramDialog>
  )

  return [
    openDlg,
    dlg,
    loading ||
      programInnerciseDlgLoading ||
      programLevelDlgLoading ||
      programCategoryDlgLoading,
  ]
}

ProgramDialog.propTypes = {
  onSubmit: PropTypes.func,
  openMediaManager: PropTypes.func,
  categories: PropTypes.array,
  access: PropTypes.object,
  openConfirmDialog: PropTypes.func.isRequired,
}

ProgramDialog.defaultProps = {
  onSubmit: null,
}

export default ProgramDialog
